// Pure date/label helpers shared by the scorecards. No React, no Date-locale
// dependence, so they are safe to unit test with node:test.

const pad2 = (n) => String(n).padStart(2, '0');

/** Returns [y, m, d] numbers from an API date (yyyy-mm-dd prefix), else null. */
function parseYmd(s) {
  if (!s || typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return [y, mo, d];
}

/** "2025-10-27" -> "27/10"; empty string when unparseable. */
export function formatDm(s) {
  const p = parseYmd(s);
  return p ? `${pad2(p[2])}/${pad2(p[1])}` : '';
}

export const RANGE_PLACEHOLDER = '—';

/**
 * "dd/mm – dd/mm"; appends "/yy" to both ends only when the years differ.
 * An incomplete range (either endpoint cleared) yields RANGE_PLACEHOLDER so
 * the date slot is never empty.
 */
export function formatRangeShort(start, end) {
  const a = parseYmd(start);
  const b = parseYmd(end);
  if (!a || !b) return RANGE_PLACEHOLDER;
  const yy = (y) => pad2(y % 100);
  if (a[0] !== b[0]) {
    return `${pad2(a[2])}/${pad2(a[1])}/${yy(a[0])} – ${pad2(b[2])}/${pad2(b[1])}/${yy(b[0])}`;
  }
  return `${pad2(a[2])}/${pad2(a[1])} – ${pad2(b[2])}/${pad2(b[1])}`;
}

/** "dd/mm/yyyy to dd/mm/yyyy" for tooltips. */
export function formatRangeFull(start, end) {
  const a = parseYmd(start);
  const b = parseYmd(end);
  if (!a || !b) return '';
  const f = (p) => `${pad2(p[2])}/${pad2(p[1])}/${p[0]}`;
  return `${f(a)} to ${f(b)}`;
}

/** ISO-8601 week number of a calendar date (UTC arithmetic, no timezone drift). */
function isoWeekOf(y, mo, d) {
  const dt = new Date(Date.UTC(y, mo - 1, d));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(dt.getUTCFullYear(), 0, 1);
  return Math.ceil(((dt.getTime() - yearStart) / 86400000 + 1) / 7);
}

/**
 * "W44" from "2025-W44", "W44", or a yyyy-mm-dd date. Empty string otherwise.
 */
export function isoWeekLabel(v) {
  if (v == null || typeof v !== 'string') return '';
  const s = v.trim();
  const w = /W(\d{1,2})\b/.exec(s);
  if (w) return `W${pad2(Number(w[1]))}`;
  const p = parseYmd(s);
  return p ? `W${pad2(isoWeekOf(p[0], p[1], p[2]))}` : '';
}

/** Prefix a caption with the picked origin/terminal label when there is one. */
export function withPick(pickLabel, text) {
  if (!pickLabel) return text;
  if (!text) return pickLabel;
  return `${pickLabel} · ${text}`;
}

/**
 * Caption lines for a tile, with the picked label always visible. The label
 * prefixes the first available line; when no line is available the label is
 * the only line. Falsy entries in `texts` are skipped, so the result lines up
 * with `texts.filter(Boolean)` whenever any text exists.
 */
export function pickCaptionLines(pickLabel, texts) {
  const avail = (texts || []).filter(Boolean);
  if (avail.length === 0) return pickLabel ? [pickLabel] : [];
  return [withPick(pickLabel, avail[0]), ...avail.slice(1)];
}
